import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import { track } from '@/lib/analytics';
import { isViewingAsSomeoneElse } from '@/lib/viewAs';
import { confirmLeave } from '@/hooks/useUnsavedGuard';
import {
  GUIDE_EVENT, ONBOARDING_EVENT, lastOnboarding, onboardingEnabled, readMeta, readProgress, saveMeta,
  saveProgress, setOnboardingActive, type GoalIntent, type OnboardingSignal, type OnboardingUser,
} from '@/lib/onboarding';
import {
  GUIDE_HOME, GUIDE_SECTION, ONBOARDING_GUIDES, onRoute, type GuideAction, type GuideCtx, type GuideStep,
} from './onboardingGuides';

/****************
 * THE WELCOME GUIDE ON SCREEN. Mishka, 2026-10-21.
 *
 * Quick Learn's look and voice -- the Orb's card, a spotlight, the counter --
 * with three differences that make it a guide that DOES rather than one that
 * explains:
 *
 *   - The spotlight is a picture, not a wall. There is no click-catcher over
 *     the page, so the lit field takes clicks and typing, and Enter, the arrows
 *     and Esc are left alone while the focus is anywhere but this card.
 *   - A step can wait for the person to do it (onboardingGuides.ts, `done`),
 *     told by the pages through lib/onboarding.ts. Next is always there too.
 *   - It follows the person rather than leading them. It never navigates on
 *     its own: on another page it says where the step is and offers "Take me
 *     there", which asks the builder's unsaved-work guard first.
 *
 * Below the pop-ups (z-index under 50): a select that opens over a lit field
 * must not open under the dimming. And it steps aside entirely while a dialog
 * is open (the AI terms, a broker key), so it never sits on top of one.
 ****************/

const SPOT_PAD = 8;
const CARD_W = 360;
const CARD_GAP = 24;
const MARGIN = 16;

interface Rect { top: number; left: number; width: number; height: number }

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** The page a route is named by in the menu (common.json). */
function pageName(step: GuideStep): string | null {
  const to = step.goTo ?? (typeof step.route === 'string' ? step.route : null);
  if (step.route instanceof RegExp) return null; // a trader's page: "open one first"
  return ({
    '/traders': 'common:nav.traders',
    '/configure': 'common:nav.strategyBuilder',
    '/copy-trade': 'common:nav.copyTrade',
    '/ai': 'common:nav.aiConnections',
  } as Record<string, string>)[to ?? ''] ?? null;
}

/** The union of whichever targets are on screen, or null. */
function findTargets(names: string[]): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (const n of names) {
    const el = document.querySelector<HTMLElement>(`[data-tour="${n}"]`);
    if (el && el.offsetWidth > 4 && el.offsetHeight > 4) out.push(el);
  }
  return out;
}

function unionRect(els: HTMLElement[]): DOMRect {
  const rs = els.map((e) => e.getBoundingClientRect());
  const left = Math.min(...rs.map((r) => r.left));
  const top = Math.min(...rs.map((r) => r.top));
  const right = Math.max(...rs.map((r) => r.right));
  const bottom = Math.max(...rs.map((r) => r.bottom));
  return new DOMRect(left, top, right - left, bottom - top);
}

/*
 * The part of the screen the element scrolls in. The page scrolls inside
 * <main>, under the header and the Phemex banner, so "on screen" is measured
 * against that box rather than the window.
 */
function visibleBox(el: HTMLElement): { top: number; bottom: number } {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) {
      const r = p.getBoundingClientRect();
      return { top: Math.max(0, r.top), bottom: Math.min(window.innerHeight, r.bottom) };
    }
  }
  return { top: 0, bottom: window.innerHeight };
}

/*
 * A window of the app that takes the whole screen is open (the AI terms, a
 * broker key, the no-cap warning): the guide steps aside. Radix marks
 * everything behind a modal data-aria-hidden; a panel like Filters, which a
 * step points into, does not, and the guide stays.
 */
const dialogOpen = () =>
  !!document.querySelector('[data-aria-hidden="true"], [role="alertdialog"][data-state="open"]');

/** The keys belong to the page while its focus is in a field or any control but this card. */
function keysBelongToPage(e: KeyboardEvent, card: HTMLElement | null): boolean {
  const el = e.target as HTMLElement | null;
  if (!el || el === document.body) return false;
  if (card && card.contains(el)) return false;
  return true;
}

/** Press the control, or the first row / button / link in it (a table's first row opens that trader). False while it is not there. */
function pressFirst(name: string): boolean {
  const el = document.querySelector<HTMLElement>(`[data-tour="${name}"]`);
  const btn = el && (el.matches('button, a, tr') ? el : el.querySelector<HTMLElement>('tr, button, a'));
  if (!btn || (btn as HTMLButtonElement).disabled) return false;
  btn.click();
  return true;
}

export function IntentGuide() {
  const { t } = useTranslation('learn');
  const { user, isLoaded } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [intent, setIntent] = useState<GoalIntent | null>(null);
  const [idx, setIdx] = useState(-1);
  const [spot, setSpot] = useState<Rect | null>(null);
  const [card, setCard] = useState({ x: 0, y: 0 });
  const [cardVisible, setCardVisible] = useState(false);
  const [hidden, setHidden] = useState(false);
  // Bumped by every page signal, so the step re-reads what it waits for.
  const [tick, setTick] = useState(0);

  const coachRef = useRef<HTMLDivElement>(null);
  const targetsRef = useRef<HTMLElement[]>([]);
  const dirRef = useRef(1);
  const entryRef = useRef(new Map<OnboardingSignal, unknown>());
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const resumedRef = useRef(false);
  // The trader's page last shown at a trader's step in this run, and a pending press of a list's first trader.
  const placeRef = useRef<{ path: string; state: unknown } | null>(null);
  const pickRef = useRef<number | undefined>(undefined);

  const steps = useMemo(() => (intent ? ONBOARDING_GUIDES[intent] : []), [intent]);
  const running = intent !== null && idx >= 0 && idx < steps.length;
  const step = running ? steps[idx] : null;
  const reduced = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const ctx = useCallback((): GuideCtx => ({
    now: <T,>(s: OnboardingSignal) => (lastOnboarding<T>(s)),
    entry: <T,>(s: OnboardingSignal) => entryRef.current.get(s) as T | undefined,
    path: pathRef.current,
  }), []);

  const snapshot = () => {
    const m = new Map<OnboardingSignal, unknown>();
    (['sb_step', 'strategy_saved', 'ct_state', 'session_started', 'ai_status', 'token_created',
      'token_copied', 'token_connected', 'copy_trader', 'dash_period', 'dash_sort', 'dash_filter'] as OnboardingSignal[])
      .forEach((s) => m.set(s, lastOnboarding(s)));
    entryRef.current = m;
  };

  const ouser = user as unknown as OnboardingUser | null | undefined;

  const end = useCallback((how: 'finished' | 'abandoned', at: GuideStep | null) => {
    if (intent) {
      track(how === 'finished' ? 'onboarding_guide_finished' : 'onboarding_guide_abandoned', intent, undefined, { step: at?.id ?? '' });
      if (ouser) {
        saveMeta(ouser, { guides: { [intent]: how } });
        saveProgress(ouser.id, null);
      }
    }
    setIntent(null);
    setIdx(-1);
    setSpot(null);
    setCardVisible(false);
    targetsRef.current = [];
    placeRef.current = null;
    window.clearInterval(pickRef.current);
  }, [intent, ouser]);

  /*
   * Move to `from`, or past it. Going forward, a step that is not for this run
   * (`when`) or already done is passed over; going back, only the first.
   */
  const goTo = useCallback((from: number, dir: number, list = steps, who = intent) => {
    if (!who) return;
    dirRef.current = dir;
    snapshot();
    const c = ctx();
    let i = from;
    while (i >= 0 && i < list.length) {
      const s = list[i];
      if (s.when && !s.when(c)) { i += dir; continue; }
      if (dir > 0 && s.done && s.done(c)) {
        track('onboarding_step_done', who, undefined, { step: s.id });
        i += 1; continue;
      }
      break;
    }
    if (i < 0) return;
    if (i >= list.length) { end('finished', list[list.length - 1] ?? null); return; }
    setIdx(i);
    if (ouser) saveProgress(ouser.id, { intent: who, step: i });
  }, [steps, intent, ctx, end, ouser]);

  /*
   * What moved the guide: the person pressing Next or Back, or the page doing
   * the thing (`done`). A press takes them to the step (see "Next takes them
   * there" below); a resume after a reload moves nothing.
   */
  const driveRef = useRef<'next' | 'back' | 'done' | null>(null);
  const builderDriveRef = useRef<'next' | 'back' | null>(null);

  const next = useCallback((how: 'next' | 'done' = 'next') => {
    if (!step || !intent) return;
    // Next on "open a trader" / "Copy Trader": press it; the step then completes on its own.
    if (how === 'next' && step.nextPress && onRoute(step, pathRef.current) && (step.nextPressWhen?.(ctx()) ?? true)) {
      if (pressFirst(step.nextPress)) return;
    }
    track('onboarding_step_done', intent, undefined, { step: step.id });
    driveRef.current = how;
    goTo(idx + 1, 1);
  }, [step, intent, idx, goTo]);

  /****************
   * TAKE ME THERE, and Next on the way to a step: the step's page, opened
   * after the builder's unsaved-work question.
   *
   * A trader's page has no one address. Mishka, 2026-10-31: on Lite's list,
   * at "Then, how they trade", Take me there opened the list already on
   * screen and Next only turned the card, so neither seemed to do anything.
   * Now it is the trader opened last in this run, or else the first one of
   * the list the step names (`pick`), pressed as that list's own step does.
   ****************/
  const takeThere = useCallback((s: GuideStep) => {
    window.clearInterval(pickRef.current);
    const re = s.route instanceof RegExp ? s.route : null;
    const last = placeRef.current;
    if (re && last && re.test(last.path)) {
      if (confirmLeave()) navigate(last.path, { state: last.state });
      return;
    }
    const to = s.goTo ?? (typeof s.route === 'string' ? s.route : null);
    if (!to) return;
    if (pathRef.current !== to) {
      if (!confirmLeave()) return;
      navigate(to);
    }
    if (!re || !s.pick) return;
    // The list may still be loading: its first trader is pressed once it shows, for a few seconds at most.
    const list = s.pick;
    let tries = 0;
    pickRef.current = window.setInterval(() => {
      tries += 1;
      if (re.test(pathRef.current) || tries > 40 || (pathRef.current === to && pressFirst(list))) window.clearInterval(pickRef.current);
    }, 150);
  }, [navigate]);
  useEffect(() => { const pending = pickRef; return () => window.clearInterval(pending.current); }, []);

  /* The trader on screen at a trader's step: Take me there comes back to this one. */
  useEffect(() => {
    if (step?.route instanceof RegExp && step.route.test(location.pathname)) {
      placeRef.current = { path: location.pathname + location.search, state: location.state };
    }
  }, [step, location]);

  /* ── Started by the welcome video, or resumed after a reload ── */
  useEffect(() => {
    const onStart = (e: Event) => {
      const d = (e as CustomEvent<{ intent: GoalIntent }>).detail;
      if (!d?.intent || !ONBOARDING_GUIDES[d.intent]) return;
      // The guide opens on its own page, not over whatever was on screen.
      const home = GUIDE_HOME[d.intent];
      if (pathRef.current !== home && confirmLeave()) navigate(home);
      setIntent(d.intent);
      setIdx(-1);
      placeRef.current = null;
      driveRef.current = 'next';
      // After the state lands: goTo reads the new list.
      window.setTimeout(() => goToRef.current(0, 1, ONBOARDING_GUIDES[d.intent], d.intent), 0);
    };
    window.addEventListener(GUIDE_EVENT, onStart);
    return () => window.removeEventListener(GUIDE_EVENT, onStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const goToRef = useRef(goTo);
  goToRef.current = goTo;

  useEffect(() => {
    if (resumedRef.current || !isLoaded || !ouser || isViewingAsSomeoneElse() || !onboardingEnabled()) return;
    resumedRef.current = true;
    const p = readProgress(ouser.id);
    if (!p || !ONBOARDING_GUIDES[p.intent] || readMeta(ouser).guides?.[p.intent]) return;
    setIntent(p.intent);
    window.setTimeout(() => goToRef.current(p.step, 1, ONBOARDING_GUIDES[p.intent], p.intent), 0);
  }, [isLoaded, ouser]);

  /* ── Quiet Orb while it runs ── */
  useEffect(() => {
    setOnboardingActive('guide', running);
    return () => setOnboardingActive('guide', false);
  }, [running]);

  /* ── The pages telling it what happened ── */
  useEffect(() => {
    if (!running) return;
    const on = () => setTick((n) => n + 1);
    window.addEventListener(ONBOARDING_EVENT, on);
    return () => window.removeEventListener(ONBOARDING_EVENT, on);
  }, [running]);

  /* What the step is showing right now: itself, or a hint on the way to it. */
  const c = ctx();
  let mode: 'step' | 'elsewhere' | 'builderAhead' | 'builderBehind' = 'step';
  let targets: string[] = step?.targets ?? [];
  if (step && !onRoute(step, location.pathname)) {
    mode = 'elsewhere';
    targets = [];
  } else if (step && step.sb !== undefined) {
    const cur = (c.now<{ step: number }>('sb_step')?.step) ?? 0;
    if (cur < step.sb) {
      mode = 'builderAhead';
      targets = cur === 0 ? ['sb-scratch', 'sb-templates'] : ['sb-continue'];
    } else if (cur > step.sb) {
      mode = 'builderBehind';
      targets = [];
    }
  }

  /*
   * Re-read the step on every signal and every page change.
   *
   * A step that is already done when it is shown (someone pressed Back to it)
   * is not passed over again at once, or Back would bounce: it waits until
   * its condition has been false and turns true again, or for Next.
   */
  const armedRef = useRef({ idx: -1, armed: true });
  useEffect(() => {
    if (!running || !step || !intent) return;
    const cc = ctx();
    if (armedRef.current.idx !== idx) armedRef.current = { idx, armed: !(step.done && step.done(cc)) };
    // Reaching the step's page counts as reaching the step: "since" starts here.
    if (step.sb !== undefined) {
      const cur = cc.now<{ step: number }>('sb_step')?.step ?? 0;
      if (onRoute(step, cc.path) && cur > step.sb && dirRef.current > 0) { next('done'); return; }
    }
    if (!step.done) return;
    const isDone = step.done(cc);
    if (!armedRef.current.armed) { if (!isDone) armedRef.current.armed = true; return; }
    if (isDone) next('done');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, location.pathname, running, idx]);

  /****************
   * NEXT TAKES THEM THERE. Mishka, 2026-10-21: "a chaque fois que je clique
   * suivant, parfois, ca ne fait pas actually suivant" -- the card changed and
   * the page did not, because the guide waited for them to change it.
   *
   * Now a press moves the screen to the step, through the app's own controls
   * so nothing is bypassed: another page is opened (after the builder's
   * unsaved-work question), the builder's own Continue or Back is pressed (its
   * no-leverage-cap warning still asks), and a step's own moving control is
   * pressed (`nextPress`: the first trader of the list, Copy Trader). What the guide
   * still never presses: Create Model, Start, a strategy, a destination, the
   * AI terms.
   *
   * When the page did the thing itself (`done`), it usually moved already
   * (Copy Trader opens Copy Trade with the trader): the guide only follows
   * if, a moment later, the screen is still elsewhere.
   ****************/
  useEffect(() => {
    if (!running || !step) return;
    const how = driveRef.current;
    driveRef.current = null;
    if (!how) return;
    if (step.sb !== undefined && how !== 'done') builderDriveRef.current = how;
    if (onRoute(step, pathRef.current)) return;
    const to = step.goTo ?? (typeof step.route === 'string' ? step.route : null);
    if (to) {
      const go = () => { if (!onRoute(step, pathRef.current)) takeThere(step); };
      if (how === 'done') {
        const timer = window.setTimeout(go, 450);
        return () => window.clearTimeout(timer);
      }
      go();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, intent]);

  /* The builder's own Continue / Back, once per press, when the step's fields are on another builder step. */
  useEffect(() => {
    const how = builderDriveRef.current;
    if (!running || !step || step.sb === undefined || !how) return;
    if (!onRoute(step, location.pathname)) return;
    const cur = lastOnboarding<{ step: number }>('sb_step')?.step;
    if (cur === undefined) return; // the builder has not said where it is yet
    builderDriveRef.current = null;
    const press = (name: string) => window.setTimeout(() => {
      document.querySelector<HTMLElement>(`[data-tour="${name}"]`)?.click();
    }, 80);
    if (how === 'next' && cur < step.sb) press(cur === 0 ? 'sb-scratch' : 'sb-continue');
    else if (how === 'back' && cur > step.sb) press('sb-back');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, location.pathname, running, idx]);

  /* Arriving on the step's page after it began: a value not known before is
     taken as it is now, so "since" counts from here. What was known stays. */
  useEffect(() => {
    if (!running || !step || !onRoute(step, location.pathname)) return;
    const prev = entryRef.current;
    snapshot();
    prev.forEach((v, k) => { if (v !== undefined) entryRef.current.set(k, v); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* ── Placement ── */
  const placeCard = useCallback((r: DOMRect | null) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const ch = coachRef.current?.offsetHeight || 250;
    if (!r) {
      const cw = Math.min(CARD_W + 60, vw - 24);
      setCard({ x: Math.round(vw / 2 - cw / 2), y: Math.round(Math.max(72, vh / 2 - ch / 2)) });
      return;
    }
    const cw = Math.min(CARD_W, vw - 24);
    let x: number, y: number;
    if (r.right + CARD_GAP + cw <= vw - MARGIN) {
      x = r.right + CARD_GAP; y = clamp(r.top - 6, MARGIN + 56, vh - ch - MARGIN);
    } else if (r.bottom + CARD_GAP + ch <= vh - MARGIN) {
      y = r.bottom + CARD_GAP; x = clamp(r.left, MARGIN + 26, vw - cw - MARGIN);
    } else if (r.top - CARD_GAP - ch >= MARGIN + 56) {
      y = r.top - CARD_GAP - ch; x = clamp(r.left, MARGIN + 26, vw - cw - MARGIN);
    } else if (r.left - CARD_GAP - cw >= MARGIN) {
      x = r.left - CARD_GAP - cw; y = clamp(r.top - 6, MARGIN + 56, vh - ch - MARGIN);
    } else {
      // No room beside it (a phone, or a card bigger than the screen): the
      // corner or edge that hides the least of it; bottom right on a tie.
      const xs = [vw - cw - MARGIN, vw / 2 - cw / 2, MARGIN];
      const ys = [vh - ch - MARGIN, MARGIN + 56];
      const hidden = (xx: number, yy: number) =>
        Math.max(0, Math.min(r.right, xx + cw) - Math.max(r.left, xx)) *
        Math.max(0, Math.min(r.bottom, yy + ch) - Math.max(r.top, yy));
      let best = Infinity;
      x = xs[0]; y = ys[0];
      for (const yy of ys) for (const xx of xs) {
        const h = hidden(xx, yy);
        if (h < best - 1) { best = h; x = xx; y = yy; }
      }
    }
    setCard({ x: Math.round(x), y: Math.round(y) });
  }, []);

  const measure = useCallback(() => {
    const els = targetsRef.current.filter((e) => e.isConnected);
    if (els.length === 0) { setSpot(null); placeCard(null); return; }
    const r = unionRect(els);
    setSpot({ top: r.top - SPOT_PAD, left: r.left - SPOT_PAD, width: r.width + SPOT_PAD * 2, height: r.height + SPOT_PAD * 2 });
    placeCard(r);
  }, [placeCard]);

  /* Find the targets: poll a moment; an optional step with nothing to show is passed over. */
  const targetKey = targets.join(' ');
  useEffect(() => {
    if (!running || !step) return;
    setCardVisible(false);
    targetsRef.current = [];
    let cancelled = false;
    let attempts = 0;
    let timer: number | undefined;
    const probe = () => {
      if (cancelled) return;
      if (targets.length === 0) { setSpot(null); placeCard(null); setCardVisible(true); return; }
      const els = findTargets(targets);
      if (els.length === 0) {
        attempts += 1;
        if (mode === 'step' && step.optional && attempts > 10) { goTo(idx + dirRef.current, dirRef.current); return; }
        // Not there yet (still loading, or appears after an action): show the card, keep looking.
        if (attempts === 8) { setSpot(null); placeCard(null); setCardVisible(true); }
        timer = window.setTimeout(probe, attempts > 25 ? 600 : 120);
        return;
      }
      targetsRef.current = els;
      const bringIntoView = () => {
        const r = unionRect(els);
        const box = visibleBox(els[0]);
        if (r.top >= box.top + 12 && r.bottom <= box.bottom - 12) return false;
        // Taller than the room it has: its top in view, the card beside it.
        const tall = r.height > (box.bottom - box.top) * 0.7;
        els[0].scrollIntoView({ block: tall ? 'start' : 'center', behavior: reduced ? 'auto' : 'smooth' });
        return true;
      };
      const settle = reduced ? 40 : 420;
      const scrolled = bringIntoView();
      timer = window.setTimeout(() => {
        if (cancelled) return;
        measure();
        setCardVisible(true);
        // The page may still be moving (a card folding after a choice): look once more.
        timer = window.setTimeout(() => {
          if (!cancelled && bringIntoView()) timer = window.setTimeout(() => { if (!cancelled) measure(); }, settle);
        }, 700);
      }, scrolled ? settle : 0);
    };
    timer = window.setTimeout(probe, 60);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, idx, targetKey, mode, location.pathname]);

  /* Keep it glued while the page scrolls, resizes, or grows as fields fill in. */
  useEffect(() => {
    if (!running) return;
    let raf = 0;
    const refresh = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (targetsRef.current.length && targetsRef.current.some((e) => !e.isConnected)) {
          targetsRef.current = findTargets(targets);
        }
        measure();
      });
    };
    window.addEventListener('resize', refresh);
    window.addEventListener('scroll', refresh, true);
    const poll = window.setInterval(() => { refresh(); setHidden(dialogOpen()); }, 400);
    return () => {
      window.removeEventListener('resize', refresh);
      window.removeEventListener('scroll', refresh, true);
      window.clearInterval(poll);
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, measure, targetKey]);

  const firstShown = steps.findIndex((s) => !s.when || s.when(c));
  const back = () => { if (idx > firstShown) { driveRef.current = 'back'; goTo(idx - 1, -1); } };

  /* Keys: only while the focus is not in the page. */
  useEffect(() => {
    if (!running) return;
    const onKey = (e: KeyboardEvent) => {
      if (hidden || e.defaultPrevented || keysBelongToPage(e, coachRef.current)) return;
      if (e.key === 'Escape') end('abandoned', step);
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!running || !step || !intent) return null;

  const shown = steps.filter((s) => !s.when || s.when(c));
  const pos = shown.indexOf(step) + 1;
  // A card in the middle: the welcome and the last card, which point at nothing.
  const centered = !step.targets?.length;
  const aiOpen = !!(c.now<{ available: boolean }>('ai_status')?.available || c.now<{ aiAvailable: boolean }>('ct_state')?.aiAvailable);

  let body = t(step.body);
  if (step.bodyAi && aiOpen) body += ' ' + t(step.bodyAi);
  let hint: string | null = null;
  if (mode === 'elsewhere') {
    const page = pageName(step);
    hint = page ? t('guide.hint.elsewhere', { page: t(page) }) : t('guide.hint.openTrader');
  } else if (mode === 'builderAhead') {
    const cur = c.now<{ step: number }>('sb_step')?.step ?? 0;
    hint = cur === 0 ? t('guide.hint.template') : t('guide.hint.continue');
  } else if (mode === 'builderBehind') {
    hint = t('guide.hint.back');
  }
  const takeMeThere = mode === 'elsewhere' ? (step.goTo ?? (typeof step.route === 'string' ? step.route : null)) : null;

  const act = (a: GuideAction) => {
    if (a === 'next') next('next');
    else if (a === 'later') end('abandoned', step);
    else if (a === 'done') end('finished', step);
    else if (a === 'ai') {
      if (!confirmLeave()) return;
      end('finished', step);
      navigate('/ai/connect');
    }
  };
  const actionLabel: Record<GuideAction, string> = {
    next: 'guide.ui.letsGo', later: 'guide.ui.later', done: 'guide.ui.done', ai: 'guide.ui.showAi',
  };
  const isLast = shown[shown.length - 1] === step;

  return (
    <div style={{ display: hidden ? 'none' : undefined }}>
      <style>{GUIDE_CSS}</style>
      {spot ? (
        <div className="og-spot" style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }} />
      ) : centered ? (
        <div className="og-dim" />
      ) : null}

      <div
        ref={coachRef}
        className={`og-coach${centered ? ' og-center' : ''}`}
        style={{ transform: `translate(${card.x}px, ${card.y}px)`, opacity: cardVisible ? 1 : 0 }}
        role="region"
        aria-live="polite"
        aria-label={t('guide.ui.aria')}
      >
        <div className="og-panel">
          <div className="og-orb"><img src="/copyfilogo.png" alt="" /></div>
          <div className="og-eyebrow">
            <span>{t(GUIDE_SECTION[intent])}</span>
            <span className="og-eyebrow-end">
              <span className="og-idx">{pos} / {shown.length}</span>
              {!step.actions && (
                <button type="button" className="og-lnk og-exit" onClick={() => end('abandoned', step)}>{t('guide.ui.exit')}</button>
              )}
            </span>
          </div>
          <h4 className="og-title">{t(step.title)}</h4>
          <p className="og-body">{body}</p>
          {hint && <p className="og-hint">{hint}</p>}

          <div className="og-foot">
            {step.actions ? (
              <div className="og-acts og-acts-end">
                {step.actions.map((a, i) => (
                  <button
                    key={a}
                    type="button"
                    className={i === step.actions!.length - 1 ? 'og-next' : 'og-lnk'}
                    onClick={() => act(a)}
                  >
                    {t(actionLabel[a])}
                  </button>
                ))}
              </div>
            ) : (
              <>
                {/* A bar rather than Quick Learn's dots: a guide runs to 18 steps. */}
                <div className="og-bar" aria-hidden="true"><i style={{ width: `${(pos / shown.length) * 100}%` }} /></div>
                <div className="og-acts">
                  {idx > firstShown && (
                    <button type="button" className="og-lnk" onClick={back}>{t('guide.ui.back')}</button>
                  )}
                  {takeMeThere && (
                    <button
                      type="button"
                      className="og-next"
                      onClick={() => takeThere(step)}
                    >
                      {t('guide.ui.takeMeThere')}
                    </button>
                  )}
                  <button type="button" className={takeMeThere ? 'og-lnk' : 'og-next'} onClick={() => next('next')}>
                    {isLast ? t('guide.ui.done') : t('guide.ui.next')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Quick Learn's card (QuickLearnTour.tsx), under the app's pop-ups and over nothing that takes clicks. */
const GUIDE_CSS = `
.og-spot {
  position: fixed; z-index: 45; border-radius: 12px; pointer-events: none;
  transition: top .45s cubic-bezier(.3,.9,.25,1), left .45s cubic-bezier(.3,.9,.25,1),
              width .45s cubic-bezier(.3,.9,.25,1), height .45s cubic-bezier(.3,.9,.25,1);
  box-shadow:
    0 0 0 2px rgba(19,128,236,0.55),
    0 0 24px 4px rgba(59,130,246,0.18),
    0 0 0 9999px rgba(4,6,10,0.62);
}
.og-spot::after {
  content: ""; position: absolute; inset: -3px; border-radius: 15px;
  border: 1px solid rgba(96,165,250,0.25);
  animation: og-breathe 3.4s ease-in-out infinite;
}
@keyframes og-breathe {
  0%, 100% { opacity: .35; transform: scale(1); }
  50%      { opacity: .9;  transform: scale(1.012); }
}
.og-dim { position: fixed; inset: 0; z-index: 45; background: rgba(4,6,10,0.7); pointer-events: none; }

.og-coach {
  position: fixed; top: 0; left: 0; z-index: 49;
  width: min(360px, calc(100vw - 24px));
  transition: transform .5s cubic-bezier(.32,.86,.28,1), opacity .25s ease;
}
.og-coach.og-center { width: min(420px, calc(100vw - 24px)); }
.og-panel {
  position: relative;
  background: linear-gradient(180deg, rgba(24,29,42,0.97), rgba(17,21,31,0.97));
  border: 1px solid rgba(96,165,250,0.22);
  border-radius: 14px;
  box-shadow: 0 18px 50px rgba(0,0,0,0.62), 0 0 0 1px rgba(0,0,0,0.4), 0 0 40px rgba(59,130,246,0.10);
  padding: 20px 20px 14px;
}
.og-center .og-panel { padding: 26px 26px 16px; }
.og-orb {
  position: absolute; left: -21px; top: -23px;
  width: 54px; height: 54px; border-radius: 50%;
  display: grid; place-items: center;
  background: rgba(10,14,22,0.92);
  box-shadow: 0 0 0 1px rgba(96,165,250,0.25), 0 0 26px rgba(59,130,246,0.45);
  animation: og-bob 3.6s ease-in-out infinite;
}
.og-orb img { width: 44px; height: 44px; object-fit: contain; }
@keyframes og-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
.og-eyebrow {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.1em;
  color: hsl(210 60% 70%); font-weight: 600; margin: 0 0 9px; padding-left: 26px;
}
.og-center .og-eyebrow { padding-left: 0; }
.og-eyebrow-end { display: inline-flex; align-items: center; gap: 12px; flex-shrink: 0; }
.og-idx { color: hsl(215 15% 48%); font-family: 'JetBrains Mono', ui-monospace, monospace; letter-spacing: .04em; }
.og-title { margin: 0 0 7px; font-size: 15.5px; font-weight: 600; letter-spacing: -0.015em; color: hsl(210 20% 96%); text-wrap: balance; }
.og-center .og-title { font-size: 20px; margin-bottom: 10px; }
.og-body { margin: 0; font-size: 13px; line-height: 1.62; color: hsl(210 16% 80%); }
.og-center .og-body { font-size: 13.5px; }
.og-hint {
  margin: 10px 0 0; font-size: 12.5px; line-height: 1.5; color: hsl(210 80% 74%);
  padding: 8px 10px; border-radius: 8px; background: rgba(19,128,236,0.10); border: 1px solid rgba(19,128,236,0.22);
}
.og-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin-top: 17px; padding-top: 13px; border-top: 1px solid rgba(255,255,255,0.07);
}
.og-bar { flex: 1; max-width: 120px; height: 3px; border-radius: 2px; background: rgba(255,255,255,0.12); overflow: hidden; }
.og-bar i { display: block; height: 100%; background: hsl(210 80% 62%); border-radius: 2px; transition: width .4s ease; }
.og-acts { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.og-acts-end { margin-left: auto; }
.og-lnk {
  background: none; border: none; cursor: pointer; font-family: inherit;
  color: hsl(215 15% 55%); font-size: 12px; padding: 4px 2px;
}
.og-lnk:hover { color: hsl(210 20% 96%); }
.og-exit { text-transform: none; letter-spacing: normal; font-weight: 400; }
.og-next {
  display: inline-flex; align-items: center; justify-content: center;
  height: 28px; padding: 0 13px; border-radius: 6px; border: none; cursor: pointer;
  background: hsl(210 85% 50%); color: #fff;
  font-size: 12px; font-weight: 500; font-family: inherit; transition: filter .18s;
}
.og-next:hover { filter: brightness(1.12); }
.og-next:focus-visible, .og-lnk:focus-visible { outline: 2px solid hsl(210 80% 62%); outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  .og-spot, .og-coach { transition: none; }
  .og-spot::after, .og-orb { animation: none; }
}
`;
